import React from 'react';
import DataCard from '../common/DataCard';
import Loader from '../common/Loader';
import Pagination from '../common/Pagination';
import { RplDTO, CourseDTO, ComplianceHistoryResponseDTO } from '../../services/apiClient';
import { ActivityTypeConstants } from '../../constants/ActivityTypeConstants';
import { CourseTypeConstants } from '../../constants/CourseTypeConstants';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import { downloadFileFromUrl } from '../../utils/downloadUtils';
import IconButton from '../common/IconButton';
import {
  Award,
  BookOpenText,
  Crosshair,
  DollarSign,
  FileText,
  GraduationCap,
  MessageCircleWarning,
  PackageSearch,
  Pen,
  Phone,
  Target,
  Trash,
} from 'lucide-react';
import Badge from '../common/Badge';
import { getBadgeColors } from '../../utils/displayUtils';
import { RplStatusConstants } from '../../constants/RplStatusConstants';
import { MemberCategoryConstants } from '../../constants/MemberCategoryConstants';
import { CourseStatusConstants } from '../../constants/CourseStatusConstants';
import { CourseMaterialTypeConstants } from '../../constants/CourseMaterialTypeConstansts';
import { isdedicatedHunter, isdedicatedSportperson } from '../../utils/complianceUtils';
import { useNavigate } from 'react-router-dom';
import {
  getEvaluationMessage,
  getEvaluationWaitMessage,
  getPracticalTargetMessage,
  getScoreStatusMessage,
  getStudyMaterialMessage,
  getTargetLink,
  shouldShowDownloadTargetButton,
  shouldShowEvaluationButton,
  shouldShowEvaluationStatusField,
  shouldShowEvaluationWaitField,
  shouldShowPracticalTarget,
  shouldShowScoreStatus,
  shouldShowStudyMaterial,
  shouldShowStudyMaterialButton,
  shouldShowSubmitTargetButton,
  shouldShowTrackingButton
} from '../../utils/courseUtils';
import { useFeedbackDrawer } from '../../context/FeedbackDrawerContext';
import { useRplApi } from '../../hooks/useRplApi';
import { useCourseApi } from '../../hooks/useCourseApi';

type DedicatedStatusItem = (RplDTO | CourseDTO) & { itemType: 'rpl' | 'course'; };

interface DedicatedStatusDataCardProps {
  data: DedicatedStatusItem[];

  handleEdit: (row: DedicatedStatusItem) => void;
  handleDelete: (row: DedicatedStatusItem) => void;

  handleMakePayment: (row: DedicatedStatusItem) => void;
  handleDownloadStudyMaterial: (row: DedicatedStatusItem) => void;
  handleScoreDrawerOpen: (row: DedicatedStatusItem) => void;
  handleDownloadCertificate: (row: DedicatedStatusItem) => void;
  handleDownloadForfeitureDocument: (row: DedicatedStatusItem) => void;
  handleForfeit: (row: DedicatedStatusItem) => void;

  currentPage: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  isLoading: boolean;

  // Ids of the row whose certificate / forfeiture document / study material is currently
  // downloading, so the loader can be shown on the individual icon instead of the whole card.
  downloadingCertificateId?: string | null;
  downloadingForfeitureId?: string | null;
  downloadingStudyMaterialId?: string | null;

  threeYearHistory: ComplianceHistoryResponseDTO | null;

  // Optional action renderers injected by the List page
  renderHeaderActions?: (row: DedicatedStatusItem) => React.ReactNode;
  renderPrimaryActions?: (row: DedicatedStatusItem) => React.ReactNode;
  renderSecondaryActions?: (row: DedicatedStatusItem) => React.ReactNode;
}

const hideContactButtonList = [
  RplStatusConstants.APPROVED,
  RplStatusConstants.PENDING,
  RplStatusConstants.PENDING_UPDATED,
  CourseStatusConstants.APPROVED,
  CourseStatusConstants.STATUS_APPROVED,
  CourseStatusConstants.PENDING,
];

const showCertificateButtonList = [
  MemberCategoryConstants.DEDICATED_HUNTER,
  MemberCategoryConstants.DEDICATED_SPORTPERSON,
  MemberCategoryConstants.DEDICATED_HUNTER_AND_SPORTPERSON,
  MemberCategoryConstants.DEDICATED_SPORTPERSON_AND_OCCASIONAL_HUNTER,
  MemberCategoryConstants.DEDICATED_HUNTER_AND_OCCASIONAL_SPORTPERSON,
];

const showApplicationReviewList = [
  RplStatusConstants.PAYMENT_RECEIVED,
  RplStatusConstants.PENDING_UPDATED,
  CourseStatusConstants.PAYMENT_RECEIVED,
];

const showFeedbackList = [
  RplStatusConstants.REVIEWED_WITH_FEEDBACK,
  RplStatusConstants.DECLINED_WITH_FEEDBACK,
  CourseStatusConstants.FEEDBACK_PROVIDED,
  CourseStatusConstants.DECLINED,
];

const DedicatedStatusDataCard: React.FC<DedicatedStatusDataCardProps> = ({
  data,
  handleEdit,
  handleDelete,

  handleMakePayment,
  handleDownloadStudyMaterial,
  handleScoreDrawerOpen,
  handleDownloadCertificate,
  handleDownloadForfeitureDocument,
  handleForfeit,

  currentPage,
  totalPages,
  setCurrentPage,
  pageSize,
  setPageSize,
  isLoading,
  downloadingCertificateId,
  downloadingForfeitureId,
  downloadingStudyMaterialId,
  threeYearHistory,
  renderHeaderActions,
  renderPrimaryActions,
  renderSecondaryActions,
}) => {
  const { showFeedback } = useFeedbackDrawer();
  const { getFeedback: getRplFeedback } = useRplApi();
  const { getFeedback: getCourseFeedback } = useCourseApi();
  const navigate = useNavigate();

  const idMatches = (rowId: string | undefined, loadingId: string | null | undefined): boolean =>
    !!rowId && !!loadingId && rowId.toLowerCase() === loadingId.toLowerCase();

  const isRpl = (item: DedicatedStatusItem): item is RplDTO & { itemType: 'rpl' } => {
    return item.itemType === 'rpl';
  };

  const isCourse = (item: DedicatedStatusItem): item is CourseDTO & { itemType: 'course' } => {
    return item.itemType === 'course';
  };

  const fetchFeedbackForItem = (item: DedicatedStatusItem) =>
    isRpl(item) ? getRplFeedback(item.id!) : getCourseFeedback(item.id!);

  const getApplicationMaterialLabel = (row: DedicatedStatusItem): string => {
    if (!isCourse(row)) {
      return 'RPL Application';
    }

    if (row.courseMaterialTypeId === CourseMaterialTypeConstants.DOWNLOAD) {
      return 'Dedicated Course (Download Material)';
    }

    if (row.courseMaterialTypeId === CourseMaterialTypeConstants.PRINT) {
      return 'Dedicated Course (Print & Ship Material)';
    }

    return 'Dedicated Course';
  };

  const getStatus = (row: DedicatedStatusItem) => {
    return isRpl(row) ? row.rplStatus : (row as CourseDTO).courseStatus;
  };

  const leftBadge = (row: DedicatedStatusItem) => {
    const status = getStatus(row);
    const base = status?.colourGrading?.colour ?? '#ffffffff';
    const { bg, text } = getBadgeColors(base, 0.18, 0.4);
    const title = status?.title || 'Unknown';

    return base ? (
      <Badge
        label={title}
        variant="custom"
        customBgColor={bg}
        customTextColor={text}
      />
    ) : (
      <span>{title}</span>
    );
  };

  const shouldShowContactAdmin = (row: DedicatedStatusItem): boolean => {
    const status = getStatus(row);
    return !hideContactButtonList.includes(status?.id!);
  };

  const isDeclinedStatus = (row: DedicatedStatusItem): boolean => {
    const status = getStatus(row);
    return status?.id === CourseStatusConstants.DECLINED;
  };

  const shouldShowFeedbackIcon = (row: DedicatedStatusItem): boolean => {
    const status = getStatus(row);
    return showFeedbackList.includes(status?.id!);
  };

  const shouldShowCertificateButton = (row: DedicatedStatusItem): boolean => {

    if (!showCertificateButtonList.includes(row.member?.memberCategoryId!))
      return false;

    // Legacy migrated records can have no resolvable course type - the discipline
    // can't be determined, so don't offer a certificate button for them here.
    if (!row.courseTypeId)
      return false;

    if (row.courseTypeId === CourseTypeConstants.DEDICATED_HUNTER_AND_SPORTS_PERSON)
      return false;

    if (row.courseTypeId === CourseTypeConstants.DEDICATED_HUNTER &&
      !(isdedicatedHunter(row.member?.memberCategoryId!) && row.member?.dateCertificateHunter))
      return false;

    if (row.courseTypeId === CourseTypeConstants.DEDICATED_SPORTS_PERSON &&
      !(isdedicatedSportperson(row.member?.memberCategoryId!) && row.member?.dateCertificateSport))
      return false;

    const status = getStatus(row);
    return status?.id === (isRpl(row) ? RplStatusConstants.APPROVED : CourseStatusConstants.APPROVED) ||
      status?.id === CourseStatusConstants.STATUS_APPROVED;
  };

  const shouldShowForfeitureDocumentButton = (row: DedicatedStatusItem): boolean => {
    const status = getStatus(row);
    return status?.id === CourseStatusConstants.FORFEITED || status?.id === RplStatusConstants.FORFEITED;
  };

  const shouldShowPaymentButton = (row: DedicatedStatusItem): boolean => {
    const status = getStatus(row);
    return status?.id === (isRpl(row) ? RplStatusConstants.PENDING : CourseStatusConstants.PENDING);
  };

  const shouldShowEditButton = (row: DedicatedStatusItem): boolean => {
    const status = getStatus(row);

    if (isRpl(row)) {
      return (
        status?.id === RplStatusConstants.PENDING ||
        status?.id === RplStatusConstants.PENDING_UPDATED ||
        status?.id === RplStatusConstants.REVIEWED_WITH_FEEDBACK ||
        status?.id === RplStatusConstants.PAYMENT_RECEIVED
      );
    } else if (isCourse(row)) {
      return (
        status?.id === CourseStatusConstants.PENDING ||
        status?.id === CourseStatusConstants.FEEDBACK_PROVIDED
      );
    } else {
      return false;
    }
  };

  const shouldShowDeleteButton = (row: DedicatedStatusItem): boolean => {
    const status = getStatus(row);
    return status?.id === (isRpl(row) ? RplStatusConstants.PENDING : CourseStatusConstants.PENDING);
  };

  const isThreeYearNonCompliant = (row: DedicatedStatusItem): boolean => {
    if (!threeYearHistory?.complianceYears || threeYearHistory.complianceYears.length < 3) {
      return false;
    }

    // Determine which activity type to check based on course type
    let activityTypeToCheck: string | null = null;

    if (row.courseTypeId === CourseTypeConstants.DEDICATED_HUNTER) {
      activityTypeToCheck = ActivityTypeConstants.HUNT_RELATED;
    } else if (row.courseTypeId === CourseTypeConstants.DEDICATED_SPORTS_PERSON) {
      activityTypeToCheck = ActivityTypeConstants.SPORT_SHOOTING;
    }

    if (!activityTypeToCheck) {
      return false;
    }

    // Check if all 3 years are non-compliant for the specific activity type
    var test = threeYearHistory.complianceYears.every((year) => {
      const certificateNeeded = activityTypeToCheck === ActivityTypeConstants.HUNT_RELATED
        ? year.certificateStatus?.hunter === 'needed'
        : year.certificateStatus?.sport === 'needed';

      const approvedActivitiesCount = year.activities.filter((a) => a.activityTypeId === activityTypeToCheck && a.activityStatusId,).length;

      var result = certificateNeeded && approvedActivitiesCount < 2

      return result;
    });

    return test;


  };

  return (
    <div>
      {isLoading ? (
        <div className="py-8 flex justify-center">
          <Loader />
        </div>
      ) : data.length === 0 ? (
        <div className="py-8 text-center text-sm text-gray-500">
          No records found
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {data.map((row) => {
            const title = row.courseType?.title || 'Unknown';
            const showWarning = isThreeYearNonCompliant(row);

            // If showing warning, render warning card instead of normal content
            if (showWarning) {
              return (
                <div
                  key={`${row.itemType}-${row.id}`}
                  className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-6 shadow-sm"
                >
                  <div className="flex items-start gap-3">
                    <MessageCircleWarning className="text-yellow-600 mt-1 flex-shrink-0" size={24} />
                    <div className="flex-1">
                      <h3 className="font-bold text-yellow-900 mb-2">
                        {title}
                      </h3>
                      <p className="text-sm text-yellow-800 mb-3">
                        Dedicated hunt/sport status not up to date. Kindly maintain your dedicated status as per the requirement.
                      </p>
                      <a
                        href="https://natshoot.co.za/dedicated-status/#maintain-dedicated-hunter-and-or-sport-person-status"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-yellow-900 underline hover:text-yellow-700"
                      >
                        View documentation
                      </a>
                    </div>
                  </div>
                </div>
              );
            }

            return (

              <DataCard<DedicatedStatusItem>
                key={`${row.itemType}-${row.id}`}
                item={row}
                leftBadgeNode={() => leftBadge(row)}
                rightMeta={() => ({
                  label: 'Applied',
                  value: (
                    <span>
                      {row.createdUtc
                        ? convertToUserTime(String(row.createdUtc), true)
                        : 'n/a'}
                    </span>
                  ),
                })}
                title={() => title}
                subtitle={() => getApplicationMaterialLabel(row)}
                headerActions={renderHeaderActions}
                fields={[
                  {
                    label: 'Payment Status',
                    render: (row) => {
                      const proofFileId = isRpl(row) ? row.proofOfPaymentFileId : (row as CourseDTO).proofOfPaymentFileId;
                      if (proofFileId) {
                        return 'Proof uploaded, thank you. Waiting for payment confirmation, this can take up to 3 business days.';
                      }
                      return 'Waiting for payment confirmation.';
                    },
                    hideIf: (row) => {
                      if (isDeclinedStatus(row)) return true;
                      const status = getStatus(row);
                      return status?.id != (isRpl(row) ? RplStatusConstants.PENDING : CourseStatusConstants.PENDING);
                    },
                  },
                  {
                    label: 'Payment Status',
                    render: (row) => `Payment received on ${convertToUserTime(String(row.paidDate), true)}, thank you.`,
                    hideIf: (row) => {
                      if (isDeclinedStatus(row)) return true;
                      const status = getStatus(row);
                      return status?.id != (isRpl(row) ? RplStatusConstants.PAYMENT_RECEIVED : CourseStatusConstants.PAYMENT_RECEIVED);
                    },
                  },
                  {
                    label: 'Application Review',
                    render: () => `We are in the process of reviewing your application, this can take up to 3 business days to complete.`,
                    hideIf: (row) => {
                      if (isDeclinedStatus(row)) return true;
                      if (!isRpl(row)) return true;
                      const status = getStatus(row);
                      return !showApplicationReviewList.includes(status?.id!);
                    }
                  },
                  {
                    label: 'Study Material',
                    render: (row) => getStudyMaterialMessage(row),
                    hideIf: (row) => {
                      if (isDeclinedStatus(row)) return true;
                      if (!isCourse(row)) return true;
                      return !shouldShowStudyMaterial(row);
                    }
                  },
                  {
                    label: 'Practical Target',
                    render: (row) => getPracticalTargetMessage(row),
                    hideIf: (row) => {
                      if (isDeclinedStatus(row)) return true;
                      if (!isCourse(row)) return true;
                      return !shouldShowPracticalTarget(row);
                    }
                  },
                  {
                    label: 'Evaluation Status',
                    render: (row) => getEvaluationMessage(row),
                    hideIf: (row) => {
                      if (isDeclinedStatus(row)) return true;
                      if (!isCourse(row)) return true;
                      return !shouldShowEvaluationStatusField(row);
                    }
                  },
                  {
                    label: 'Evaluation',
                    render: (row) => getEvaluationWaitMessage(row),
                    hideIf: (row) => {
                      if (isDeclinedStatus(row)) return true;
                      if (!isCourse(row)) return true;
                      return !shouldShowEvaluationWaitField(row);
                    }
                  },
                  {
                    label: 'Practical Status',
                    render: (row) => getScoreStatusMessage(row),
                    hideIf: (row) => {
                      if (isDeclinedStatus(row)) return true;
                      if (!isCourse(row)) return true;
                      return !shouldShowScoreStatus(row);
                    }
                  },
                  {
                    label: 'Application Status',
                    render: () => `Your application has been successfully submitted and is now pending approval by Natshoot. Please allow up to 3 business days.`,
                    hideIf: (row) => {
                      if (isDeclinedStatus(row)) return true;
                      if (!isCourse(row)) return true;
                      const status = getStatus(row);
                      return status?.id !== CourseStatusConstants.STATUS_PENDING;
                    },
                  },
                  {
                    label: 'Application Status',
                    render: () => `Score uploaded, please allow 3 business days for Natshoot to evaluate your scores. After 3 business days please see feedback under Scores.`,
                    hideIf: (row) => {
                      if (isDeclinedStatus(row)) return true;
                      if (!isCourse(row)) return true;
                      const status = getStatus(row);
                      return status?.id !== CourseStatusConstants.SCORES_SUBMITTED;
                    },
                  },
                  {
                    label: 'Application Status',
                    render: () => `Your application has been approved and dedicated status has been awarded.You can now download your certificate.`,
                    hideIf: (row) => {
                      if (isDeclinedStatus(row)) return true;
                      const status = getStatus(row);
                      return status?.id != (isRpl(row) ? RplStatusConstants.APPROVED : CourseStatusConstants.APPROVED);
                    },
                  },
                  {
                    label: 'Action Required',
                    render: () =>
                      `Feedback has been provided on your record. Please review it and take the necessary action to update your information accordingly.`,
                    hideIf: (row) => {
                      if (isDeclinedStatus(row)) return true;
                      const status = getStatus(row);
                      return !showFeedbackList.includes(status?.id!);
                    },
                  },
                  {
                    label: 'Forfeiture Date',
                    render: (row) => row.forfeitureDate ? convertToUserTime(String(row.forfeitureDate), true) : '-',
                    hideIf: (row) => {
                      const status = getStatus(row);
                      return status?.id !== CourseStatusConstants.FORFEITED && status?.id !== RplStatusConstants.FORFEITED;
                    },
                  },
                  {
                    label: 'Forfeiture Reason',
                    render: (row) => row.forfeitureReason?.title ?? '-',
                    hideIf: (row) => {
                      const status = getStatus(row);
                      return status?.id !== CourseStatusConstants.FORFEITED && status?.id !== RplStatusConstants.FORFEITED;
                    },
                  },
                  {
                    label: 'Other Reason',
                    render: (row) => row.forfeitureOtherReason ?? '-',
                    hideIf: (row) => {
                      const status = getStatus(row);
                      if (status?.id !== CourseStatusConstants.FORFEITED && status?.id !== RplStatusConstants.FORFEITED) return true;
                      return !row.forfeitureOtherReason;
                    },
                  },
                  {
                    label: 'Location',
                    render: (row) => row.forfeitureSignedAtLocation ?? '-',
                    hideIf: (row) => {
                      const status = getStatus(row);
                      return status?.id !== CourseStatusConstants.FORFEITED && status?.id !== RplStatusConstants.FORFEITED;
                    },
                  },
                  {
                    label: 'Section 16 Firearm Licence',
                    render: (row) => row.forfeitureHasSection16Licence === true ? 'Yes' : row.forfeitureHasSection16Licence === false ? 'No' : '-',
                    hideIf: (row) => {
                      const status = getStatus(row);
                      if (status?.id !== CourseStatusConstants.FORFEITED && status?.id !== RplStatusConstants.FORFEITED) return true;
                      return row.forfeitureHasSection16Licence == null;
                    },
                  },
                ]}
                files={[]}
                primaryActions={
                  renderPrimaryActions ??
                  ((row) => (
                    <div className="flex justify-between w-full">
                      <div>
                        <div className="flex gap-1">
                          {shouldShowContactAdmin(row) && (
                            <IconButton
                              label="Contact Admin"
                              color="blue"
                              icon={<Phone />}
                              onClick={() => navigate('/contact')}
                            />
                          )}
                          {shouldShowFeedbackIcon(row) && (
                            <IconButton
                              color="blue"
                              icon={<MessageCircleWarning />}
                              onClick={() => showFeedback(() => fetchFeedbackForItem(row))}
                              label={'Feedback'}
                            />
                          )}

                          {isCourse(row) && !isDeclinedStatus(row) && (
                            <>
                              {shouldShowTrackingButton(row) && (
                                <IconButton
                                  color="blue"
                                  icon={<PackageSearch />}
                                  onClick={() => window.open(`https://portal.thecourierguy.co.za/track?ref=${row.courierTrackingReference}`, "_blank")}
                                  label={'Track Your Package'}
                                />
                              )}
                            </>
                          )}

                        </div>
                        {shouldShowPaymentButton(row) && (
                          <IconButton
                            color="green"
                            icon={<DollarSign />}
                            onClick={() => handleMakePayment(row)}
                            label={'Make a payment'}
                          />
                        )}
                      </div>
                      <div className="flex gap-1">
                        {!isDeclinedStatus(row) && (
                          <>
                            {shouldShowEditButton(row) && (
                              <IconButton
                                color="orange"
                                icon={<Pen />}
                                onClick={() => handleEdit(row)}
                                label={'Edit'}
                              />
                            )}
                            {shouldShowDeleteButton(row) && (
                              <IconButton
                                color="red"
                                icon={<Trash />}
                                onClick={() => handleDelete(row)}
                                label={'Delete'}
                              />
                            )}
                            {shouldShowForfeitureDocumentButton(row) && (
                              <IconButton
                                color="blue"
                                icon={<FileText />}
                                isLoading={idMatches(row.id, downloadingForfeitureId)}
                                onClick={() => handleDownloadForfeitureDocument(row)}
                                label={'Download Forfeiture Document'}
                              />
                            )}
                            {shouldShowCertificateButton(row) && (
                              <>
                                <IconButton
                                  color="green"
                                  icon={<Award />}
                                  isLoading={idMatches(row.id, downloadingCertificateId)}
                                  onClick={() => handleDownloadCertificate(row)}
                                  label={'Download Certificate'}
                                />
                                <IconButton
                                  color="red"
                                  icon={
                                    <span className="relative inline-flex items-center justify-center">
                                      <Award />
                                      <span className="pointer-events-none absolute left-1/2 top-1/2 h-[2px] w-[135%] -translate-x-1/2 -translate-y-1/2 rotate-45 rounded-full bg-current" />
                                    </span>
                                  }
                                  onClick={() => handleForfeit(row)}
                                  label={'Forfeit Dedicated Status'}
                                />
                              </>
                            )}
                            {isCourse(row) && (
                              <>
                                {shouldShowStudyMaterialButton(row) && (
                                  <IconButton
                                    color="orange"
                                    icon={<BookOpenText />}
                                    isLoading={idMatches(row.id, downloadingStudyMaterialId)}
                                    onClick={() => handleDownloadStudyMaterial(row)}
                                    label={'Download Study Material'}
                                  />
                                )}
                                {shouldShowDownloadTargetButton(row) && (
                                  <IconButton
                                    color="orange"
                                    icon={<Target />}
                                    onClick={() => {
                                      const targetUrl = getTargetLink(row);
                                      const fileName = `practical-target-${row.courseType?.title || 'target'}.pdf`;
                                      downloadFileFromUrl(targetUrl, fileName);
                                    }}
                                    label={'Download Practical Target'}
                                  />
                                )}
                                {shouldShowSubmitTargetButton(row) && (
                                  <IconButton
                                    color="green"
                                    icon={<Crosshair />}
                                    onClick={() => handleScoreDrawerOpen(row)}
                                    label={'Submit your practical target score. You may resubmit if declined or incorrect.'}
                                  />
                                )}
                                {shouldShowEvaluationButton(row) && (
                                  <IconButton
                                    color="green"
                                    icon={<GraduationCap />}
                                    onClick={() => navigate(`/admin/courses/evaluation/${row.id}`)}
                                    label={'Start Evaluation'}
                                  />
                                )}
                              </>
                            )}
                          </>
                        )}

                      </div>
                    </div>
                  ))
                }
                secondaryActions={renderSecondaryActions}
              />
            );
          })}
        </div>
      )}

      {!isLoading && data.length > 0 && (
        <div className="mt-4">
          <Pagination
            currentPage={
              Number.isFinite(currentPage) && currentPage > 0 ? currentPage : 1
            }
            totalPages={
              Number.isFinite(totalPages) && totalPages > 0 ? totalPages : 1
            }
            onPageChange={setCurrentPage}
            pageSize={Number.isFinite(pageSize) && pageSize > 0 ? pageSize : 12}
            onPageSizeChange={setPageSize}
          />
        </div>
      )}
    </div>
  );
};

export default DedicatedStatusDataCard;
